import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ParametroClienteRow, ParametroStatRow } from '../types/client';
import { api } from '../api';

const ParametrosTab: React.FC = () => {
  const navigate = useNavigate();
  const [paramSearch, setParamSearch] = useState<string>('');
  const [statsParametros, setStatsParametros] = useState<ParametroStatRow[]>([]);

  const [modalParam, setModalParam] = useState<string | null>(null);
  const [modalClientes, setModalClientes] = useState<ParametroClienteRow[]>([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalSearch, setModalSearch] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.getStatsParametros().then((res) => setStatsParametros(res.data)).catch(() => {});
  }, []);

  const parameterActiveCounts = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const row of statsParametros) {
      acc[row.codparametro] = row.total_clientes;
    }
    return acc;
  }, [statsParametros]);

  const filteredParams = useMemo(() => {
    const q = paramSearch.trim().toLowerCase();
    const all = Object.keys(parameterActiveCounts).sort((a, b) => a.localeCompare(b, 'es'));
    if (!q) return all;
    return all.filter((p) => p.toLowerCase().includes(q));
  }, [paramSearch, parameterActiveCounts]);

  const filteredModalClientes = useMemo(() => {
    const q = modalSearch.trim().toLowerCase();
    if (!q) return modalClientes;
    return modalClientes.filter(
      (c) => c.nombre.toLowerCase().includes(q) || c.codigocliente.toLowerCase().includes(q)
    );
  }, [modalClientes, modalSearch]);

  function openModal(codparametro: string) {
    setModalParam(codparametro);
    setModalClientes([]);
    setModalSearch('');
    setModalLoading(true);
    api.getClientesByParametro(codparametro)
      .then((res) => setModalClientes(res.data))
      .catch(() => {})
      .finally(() => setModalLoading(false));
  }

  function closeModal() {
    setModalParam(null);
    setModalClientes([]);
    setModalSearch('');
  }

  function handleOverlayClick(e: React.MouseEvent) {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) closeModal();
  }

  useEffect(() => {
    if (!modalParam) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeModal(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [modalParam]);

  return (
    <div className="tab-content active">
      <div className="metric params-table-card">
        <table>
          <thead>
            <tr>
              <th>
                <div className="table-search">
                  <input
                    className="ui-input ui-input-search"
                    value={paramSearch}
                    onChange={(e) => setParamSearch(e.target.value)}
                    placeholder="Buscar"
                  />
                </div>
              </th>
              <th style={{ width: 120 }}>Activos</th>
            </tr>
          </thead>
          <tbody>
            {filteredParams.map((p) => (
              <tr key={p} className="param-row-clickable" onClick={() => openModal(p)}>
                <td>{p}</td>
                <td>{String(parameterActiveCounts[p] ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalParam && (
        <div className="param-modal-overlay" onClick={handleOverlayClick}>
          <div className="param-modal" ref={modalRef}>
            <div className="param-modal-header">
              <div className="param-modal-title">{modalParam}</div>
              <button className="param-modal-close" onClick={closeModal}>✕</button>
            </div>
            <div className="param-modal-search">
              <input
                className="ui-input ui-input-search"
                value={modalSearch}
                onChange={(e) => setModalSearch(e.target.value)}
                placeholder="Buscar cliente"
                autoFocus
              />
            </div>
            <div className="param-modal-body">
              {modalLoading ? (
                <div className="param-modal-empty">Cargando…</div>
              ) : filteredModalClientes.length === 0 ? (
                <div className="param-modal-empty">Sin resultados</div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: 120 }}>Código</th>
                      <th>Cliente</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredModalClientes.map((c) => (
                      <tr
                        key={c.codigocliente}
                        className="param-row-clickable"
                        onClick={() => navigate(`/cliente/${encodeURIComponent(c.codigocliente)}`)}
                      >
                        <td>{c.codigocliente}</td>
                        <td>{c.nombre}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="param-modal-footer">
              {!modalLoading && `${filteredModalClientes.length} cliente${filteredModalClientes.length !== 1 ? 's' : ''}`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParametrosTab;
